var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { LoadingContextProvider, MessagesProvider, useLoadingContext, useLaunchDarklyFlags } from "@lululemon/auth-common";
import { AuthSdk, getAuthSdk } from "@lululemon/auth-sdk";
import { Modal } from "@lululemon/ecom-pattern-library-modal";
import { lazy, Suspense, useEffect, useLayoutEffect, useState } from "react";
import { ContinueAsGuest, TermsAndConditions } from "../components";
import { ENROLL_PROFILE_FORM_NAME, IDENTIFY_FORM_NAME } from "../siginin-widget/constants/okta-constants";
import { ModeView } from "./components/mode-view";
import { DETECT_ACCOUNT_MODES } from "../siginin-widget/constants/mode-names";
import { useModalEvents, useModalState, useReloadShape, useRouteChange } from "./hooks";
import styles from "./signin-modal.module.scss";
import { modeOptions } from "../siginin-widget/configs/mode-options";
var SigninWidget = lazy(function () {
    return import(
    /* webpackChunkName: "LoginV3"*/
    "../siginin-widget/signin-widget");
});
var SigninModalContent = function (_a) {
    var _b;
    var isOpen = _a.isOpen, signinWidgetProps = _a.signinWidgetProps, additionalProps = _a.additionalProps, activeFlow = _a.activeFlow, handleCloseModal = _a.handleCloseModal, setHasError = _a.setHasError, modeOption = _a.modeOption, isDetectAccountEnabled = _a.isDetectAccountEnabled;
    var _c = useState(false), isWidgetReady = _c[0], setIsWidgetReady = _c[1];
    var _d = useLoadingContext(), LoadingComponent = _d.LoadingComponent, isLoading = _d.isLoading, setIsLoading = _d.setIsLoading;
    // hide DETECT ACCOUNT modal content while transfering to password step
    var _e = useState(DETECT_ACCOUNT_MODES.includes(additionalProps === null || additionalProps === void 0 ? void 0 : additionalProps.mode)), hideModalContent = _e[0], setHideModalContent = _e[1];
    useLayoutEffect(function () {
        //should set loading to true before render to avoid content flickering. isLoading is set back to false inside dom-manipulations
        setIsLoading(true);
    }, []);
    var shouldRenderMode = Boolean((additionalProps === null || additionalProps === void 0 ? void 0 : additionalProps.mode) &&
        ((_b = modeOption === null || modeOption === void 0 ? void 0 : modeOption.showModesOnForms) === null || _b === void 0 ? void 0 : _b.includes(activeFlow)) &&
        (!isDetectAccountEnabled || activeFlow !== ENROLL_PROFILE_FORM_NAME));
    var renderContinueAsGuest = !isLoading && (additionalProps === null || additionalProps === void 0 ? void 0 : additionalProps.showContinueAsGuest);
    var onError = function () {
        setHideModalContent(false);
        setHasError(true);
    };
    useEffect(function () {
        if (isWidgetReady && activeFlow !== IDENTIFY_FORM_NAME) {
            setHideModalContent(false);
        }
    }, [activeFlow, isWidgetReady]);
    return (<Suspense fallback={<LoadingComponent />}>
      {isOpen ? (<>
          {hideModalContent && <LoadingComponent />}
          <div className={hideModalContent ? styles.transparent : ""}>
            {shouldRenderMode && (modeOption === null || modeOption === void 0 ? void 0 : modeOption.modeMessages) ? (<ModeView mode={additionalProps === null || additionalProps === void 0 ? void 0 : additionalProps.mode}/>) : null}
            <SigninWidget {...signinWidgetProps} onReady={function () {
                setIsWidgetReady(true);
            }} onError={onError}/>
            {renderContinueAsGuest && (<ContinueAsGuest onClick={handleCloseModal} {...additionalProps}/>)}
            <TermsAndConditions shouldShow={!isLoading}/>
          </div>
        </>) : null}
    </Suspense>);
};
export var SigninModal = function (_a) {
    var oktaConfig = _a.oktaConfig, locale = _a.locale, _b = _a.env, env = _b === void 0 ? "prod" : _b;
    var _c = useModalState(), isOpen = _c.isOpen, openModal = _c.openModal, resetState = _c.resetState, additionalProps = _c.additionalProps;
    var _d = useState(""), activeFlow = _d[0], setActiveFlow = _d[1];
    var _e = useState(false), hasError = _e[0], setHasError = _e[1];
    var authSdk = getAuthSdk({ oktaConfig: oktaConfig, env: env });
    var modeOption = modeOptions[(additionalProps === null || additionalProps === void 0 ? void 0 : additionalProps.mode) || ""];
    var flags = useLaunchDarklyFlags().flags;
    var isDetectAccountEnabled = Boolean(flags === null || flags === void 0 ? void 0 : flags["is-detect-account-enabled"]);
    var signinWidgetProps = __assign(__assign({ oktaConfig: oktaConfig, env: env, locale: locale, isModal: true, onFormChange: function (formName) { return setActiveFlow(formName); } }, modeOption), additionalProps);
    if (isDetectAccountEnabled) {
        delete signinWidgetProps.createAccountOpen; // Do not proceed to create account form when isDetectAccountEnabled is true. This is handled by dom-manipulation
    }
    var handleCloseModal = function () {
        var _a;
        (_a = additionalProps === null || additionalProps === void 0 ? void 0 : additionalProps.onClose) === null || _a === void 0 ? void 0 : _a.call(additionalProps);
        if (DETECT_ACCOUNT_MODES.includes(additionalProps === null || additionalProps === void 0 ? void 0 : additionalProps.mode) ||
            hasError) {
            AuthSdk.removeIdentifiers();
            authSdk.authClient.client.transactionManager.clear();
            setHasError(false);
        }
        resetState();
    };
    useReloadShape({ shouldReload: isOpen });
    useModalEvents({ openModal: openModal, closeModal: resetState });
    useRouteChange({ onRouteChange: handleCloseModal });
    return (<Modal classes={{
            modal: styles.loginModal
        }} visible={isOpen} onRequestClose={handleCloseModal} data-testid="login-modal-container">
      <div className={styles.widgetContainer}>
        <MessagesProvider>
          <LoadingContextProvider>
            <SigninModalContent signinWidgetProps={__assign({}, signinWidgetProps)} isOpen={isOpen} additionalProps={additionalProps} activeFlow={activeFlow} modeOption={modeOption} handleCloseModal={handleCloseModal} setHasError={setHasError} isDetectAccountEnabled={isDetectAccountEnabled}/>
          </LoadingContextProvider>
        </MessagesProvider>
      </div>
    </Modal>);
};
export default SigninModal;
